import type { UserSettings } from "@common/types/User";

export const GRAMS_PER_OZ = 28.3495;
export const GRAMS_PER_LB = 453.592;
export const GRAMS_PER_KG = 1000;
export const OZ_PER_LB = 16;

type UnitLabel = "short" | "long";

export interface IWeightFormatter {
  fromDisplayUnit: (value: number, unit?: string) => number;
  toDisplayUnit: (grams: number) => { value: number; unit: string };
  toSpecificUnit: (grams: number, unit: string) => number;
  alternateUnit: (currentUnit: string) => string;
  toLabelled: (grams: number, labelLength?: UnitLabel) => string;
  displayUnit: (grams?: number) => string;
}

export class ImperialWeightFormatter implements IWeightFormatter {
  static LABELS = {
    lb: { long: "pounds", short: "lb" },
    oz: { long: "ounces", short: "oz" }
  };

  toDisplayUnit(grams: number) {
    const unit = this.displayUnit(grams);
    return { unit, value: this.toSpecificUnit(grams, unit) };
  }

  fromDisplayUnit(value: number, unit: string = "oz") {
    return unit === "lb" ? value * GRAMS_PER_LB : value * GRAMS_PER_OZ;
  }

  toSpecificUnit(grams: number, unit: string) {
    const divisor = unit === "lb" ? GRAMS_PER_LB : GRAMS_PER_OZ;
    return Math.round((grams / divisor) * 100) / 100;
  }

  alternateUnit(currentUnit: string) {
    return currentUnit === "lb" ? "oz" : "lb";
  }

  toLabelled(grams: number, labelLength: UnitLabel = "short") {
    const { unit, value } = this.toDisplayUnit(grams);
    const label =
      unit === "lb"
        ? ImperialWeightFormatter.LABELS.lb[labelLength]
        : ImperialWeightFormatter.LABELS.oz[labelLength];
    return `${value} ${label}`;
  }

  // Compare the rounded oz value, not raw grams: 1 lb stored as 453.59 g is just below GRAMS_PER_LB.
  displayUnit(grams?: number) {
    if (grams !== undefined && this.toSpecificUnit(grams, "oz") >= OZ_PER_LB) return "lb";
    return "oz";
  }
}

export class MetricWeightFormatter implements IWeightFormatter {
  static LABELS = {
    g: { long: "grams", short: "g" },
    kg: { long: "kilograms", short: "kg" }
  };

  toDisplayUnit(grams: number) {
    if (grams >= GRAMS_PER_KG) {
      return {
        unit: "kg",
        value: Math.round((grams / GRAMS_PER_KG) * 100) / 100
      };
    }
    return { unit: "g", value: Math.round(grams * 100) / 100 };
  }

  fromDisplayUnit(value: number, unit: string = "g") {
    return unit === "kg" ? value * GRAMS_PER_KG : value;
  }

  toSpecificUnit(grams: number, unit: string) {
    const divisor = unit === "kg" ? GRAMS_PER_KG : 1;
    return Math.round((grams / divisor) * 100) / 100;
  }

  alternateUnit(currentUnit: string) {
    return currentUnit === "kg" ? "g" : "kg";
  }

  toLabelled(grams: number, labelLength: UnitLabel = "short") {
    const { unit, value } = this.toDisplayUnit(grams);
    const label =
      unit === "kg"
        ? MetricWeightFormatter.LABELS.kg[labelLength]
        : MetricWeightFormatter.LABELS.g[labelLength];
    return `${value} ${label}`;
  }

  displayUnit(grams?: number) {
    if (grams !== undefined && grams >= GRAMS_PER_KG) return "kg";
    return "g";
  }
}

export class WeightFormatter {
  private constructor() {
    throw new Error("Use WeightFormatter.build()");
  }

  static build(unitPreference: UserSettings["unitPreference"]) {
    return unitPreference === "imperial"
      ? new ImperialWeightFormatter()
      : new MetricWeightFormatter();
  }
}
